import { SaneImage } from 'components/sanity/sane-image';
import { SaneBlockChakra } from 'components/sanity/sane-block';
import React from 'react';

interface Span {
  _type: 'span';
  marks: string[];
  text: string;
  _key: string;
}

export interface Block {
  _type: 'block';
  style?: string;
  _key: string;
  markDefs: Array<{
    _type: string;
    _key: string;
    reference?: {
      _ref: string;
      _type: string;
    };
    blank?: boolean;
    slug?: {
      current: string;
      _type: string;
    };
  }>;
  children: Span[];
  headerTitle?: string;
  heroTitle?: string;
  headerBoldTitle?: string;
  headerSubheader?: string;
  quoteBody?: string;
  quoteTitle?: string;
  quoteName?: string;
  resourcesDescription?: string;
  background?: boolean;
  bulletsProp?: string;
  bulletsLeftFirstHeader?: string;
  bulletsLeftSecondHeader?: string;
  bulletsLeftSecondBody?: string;
}

export interface BulletItem {
  id: number;
  name: string;
  description: React.ReactNode; // Use React.ReactNode instead of any
  icon: React.FC<React.SVGProps<SVGSVGElement>> | null;
}

interface RichTextBlock {
  _type: 'block';
  style: string;
  _key: string;
  markDefs: Array<{
    _type: string;
    _key: string;
    reference?: {
      _ref: string;
      _type: string;
    };
    blank?: boolean;
    slug?: {
      current: string;
      _type: string;
    };
  }>;
  children: Array<{
    _type: 'span';
    marks: string[];
    text: string;
    _key: string;
  }>;
}

export interface RichTextItem {
  _type: 'richTextItem';
  _key: string;
  richText: RichTextBlock[];
  key: string;
}

export interface Props {
  preview: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  text: any;
  image: {
    clerk: {
      _type: 'image';
      alt: string;
      asset: {
        _ref: string;
        _type: 'reference';
      };
    };
    logo: {
      _type: 'image';
      alt: string;
      asset: {
        _ref: string;
        _type: 'reference';
      };
    };
    imageRight: {
      _type: 'image';
      alt: string;
      asset: {
        _ref: string;
        _type: 'reference';
      };
    };
    imageLeft: {
      alt: string;
      asset: {
        _type: 'reference';
        _ref: string;
      };
      _type: 'image';
    };
    bulletsRightImage: {
      asset: {
        _ref: string;
        _type: 'reference';
      };
      _type: 'image';
      alt: string;
    };
    bulletsLeftImage: {
      _type: 'image';
      alt: string;
      asset: {
        _type: 'reference';
        _ref: string;
      };
    };
  };
  code: Record<string, unknown>;
  json: Record<string, unknown>;
  body: RichTextItem[];
  seo: {
    title: string;
    twitterHandle: string;
    description: string;
    siteName: string;
    ogImage: {
      _type: 'image';
      asset: {
        _ref: string;
        _type: 'reference';
      };
    };
    siteUrl: string;
  };
  icons: (React.FC<React.SVGProps<SVGSVGElement>> | null)[];
  imageWidth: number;
  background?: boolean;
  bulletsProp?: BulletItem[] | null;
  docs?: string;
  tutorial?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function BulletsLeft(props: any) {
  const { image, text, body, icons, background, imageWidth, bulletsProp } =
    props;

  const { richText } = body.filter((textItem: RichTextItem) => {
    return textItem.key === 'bulletsLeftDescription';
  })[0];

  const bullets = bulletsProp
    ? bulletsProp
    : [
        {
          id: 1,
          name: text.bulletsLeftFirstHeader,
          description: <p>{text.bulletsLeftFirstBody}</p>,
          icon: icons[0]
        },
        {
          id: 2,
          name: text.bulletsLeftSecondHeader,
          description: <p>{text.bulletsLeftSecondBody}</p>,
          icon: icons[1]
        }
      ];

  return (
    <div className="overflow-hidden py-8 lg:py-14">
      <div className="relative mx-auto max-w-xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-8">
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {text.bulletsLeftHeader}
            </h2>
            {richText.map((textItem: RichTextBlock, index: number) => (
              <div key={index} className="mt-3 max-w-3xl text-lg text-gray-500">
                <SaneBlockChakra blocks={textItem} />
              </div>
            ))}
            <div className="mt-10 space-y-10">
              {bullets.map((item: BulletItem) => {
                return (
                  <div key={item.id} className="relative">
                    <div>
                      {item.icon && (
                        <div className="absolute flex h-12 w-12 items-center justify-center rounded-md bg-clerk-blue text-white">
                          <item.icon className="h-6 w-6" aria-hidden="true" />
                        </div>
                      )}
                      <h3 className="ml-16 text-xl font-medium leading-6 text-gray-900">
                        {item.name}
                      </h3>
                    </div>
                    <div className="mt-2 ml-16 text-lg text-gray-500">
                      {item.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div
              className={
                background
                  ? 'my-5 rounded-lg bg-white shadow-lg lg:my-0'
                  : 'my-5 lg:my-0'
              }
            >
              <div className="m-1 rounded-lg">
                <SaneImage src={image.bulletsLeftImage} width={imageWidth} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
